import { useEffect, useState } from "react";
import { Sliders } from "lucide-react";
import styles from "@/components/shop/ShopDesignTuner.module.css";

export interface TomorrowTunerState {
  heroAngle: number; // degrees
  heroGlow: number; // percent of the forest glow behind the page
  sectionGap: number; // px
  gridGap: number; // px
}

export const TOMORROW_TUNER_DEFAULTS: TomorrowTunerState = { heroAngle: 135, heroGlow: 18, sectionGap: 56, gridGap: 16 };

const SLIDERS: { key: keyof TomorrowTunerState; label: string; min: number; max: number; step: number; unit: string }[] = [
  { key: "heroAngle", label: "Hero angle", min: 90, max: 180, step: 5, unit: "°" },
  { key: "heroGlow", label: "Hero glow", min: 0, max: 40, step: 2, unit: "%" },
  { key: "sectionGap", label: "Section gap", min: 24, max: 96, step: 4, unit: "px" },
  { key: "gridGap", label: "Grid gap", min: 8, max: 64, step: 2, unit: "px" },
];

/** Live tuner for the Tomorrow Co page. Visible only with ?tune=1 (owner's standing rule: every
 *  visual-design feature ships with a tuner); "Copy values" hands the numbers back. */
export default function TomorrowDesignTuner({
  state,
  setState,
}: {
  state: TomorrowTunerState;
  setState: (next: TomorrowTunerState) => void;
}) {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setVisible(new URLSearchParams(window.location.search).get("tune") === "1");
  }, []);

  if (!visible) return null;

  return (
    <div className={styles.tuner}>
      <button className={styles.toggle} type="button" onClick={() => setOpen((v) => !v)} aria-label="Toggle Tomorrow design tuner">
        <Sliders size={12} />
        {open ? "Close" : "Tune"}
      </button>
      {open && (
        <div className={styles.panel}>
          <div className={styles.title}>Tomorrow Design Tuner</div>
          {SLIDERS.map((s) => (
            <div className={styles.row} key={s.key}>
              <div className={styles.rowLabel}>
                <span>{s.label}</span>
                <span>{`${state[s.key]}${s.unit}`}</span>
              </div>
              <input
                className={styles.slider}
                type="range"
                aria-label={s.label}
                min={s.min}
                max={s.max}
                step={s.step}
                value={state[s.key]}
                onChange={(e) => setState({ ...state, [s.key]: Number(e.target.value) })}
              />
            </div>
          ))}
          <button className={styles.reset} type="button" onClick={() => void navigator.clipboard?.writeText(JSON.stringify(state))}>
            Copy values
          </button>
          <button className={styles.reset} type="button" onClick={() => setState(TOMORROW_TUNER_DEFAULTS)}>
            Reset
          </button>
        </div>
      )}
    </div>
  );
}
